import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, MapPin, Share2, X } from 'lucide-react';
import type { RideDetail, User } from '@ridemaadi/types';
import { Button } from '@ridemaadi/ui';
import { ConfettiBurst } from './ConfettiBurst';
import {
  firstNameFromDisplayName,
  formatCompletionRoute,
  formatCompletionStatCells,
  generateCompletionAchievement,
  generateRideMemory,
  getCompletionStats,
  markCelebrationShown,
} from './celebrationUtils';

export function RideCompletionCelebration({
  ride,
  user,
  onDismiss,
}: {
  ride: RideDetail;
  user: User | null;
  onDismiss: () => void;
}) {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [confetti, setConfetti] = useState(true);

  const stats = getCompletionStats(ride);
  const [distance, duration, riders, stops] = formatCompletionStatCells(stats);
  const route = formatCompletionRoute(ride);
  const achievement = generateCompletionAchievement(ride, stats);
  const memory = generateRideMemory(ride, stats);
  const firstName = firstNameFromDisplayName(user?.displayName);

  useEffect(() => {
    const enter = window.requestAnimationFrame(() => setVisible(true));
    const confettiOff = window.setTimeout(() => setConfetti(false), 1600);
    return () => {
      window.cancelAnimationFrame(enter);
      window.clearTimeout(confettiOff);
    };
  }, []);

  const dismiss = () => {
    markCelebrationShown(ride.id);
    onDismiss();
  };

  const viewSummary = () => {
    markCelebrationShown(ride.id);
    onDismiss();
    navigate(`/rides/${ride.id}`);
  };

  const shareRide = async () => {
    const url = `${window.location.origin}/j/${ride.shareSlug}`;
    const text = `${ride.title} - completed on RideMaadi`;
    if (navigator.share) {
      await navigator.share({ title: ride.title, text, url }).catch(() => undefined);
      return;
    }
    await navigator.clipboard.writeText(url).catch(() => undefined);
  };

  const planNext = () => {
    dismiss();
    navigate('/rides/new');
  };

  const riderPhrase =
    stats.riderCount === 1
      ? '1 rider completed another'
      : `${stats.riderCount} riders completed another`;

  return (
    <div
      className="fixed inset-0 z-[120] flex flex-col overflow-hidden bg-bg/95 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ride-complete-title"
    >
      <ConfettiBurst active={confetti} />

      <button
        type="button"
        onClick={dismiss}
        className="absolute right-3 top-[calc(env(safe-area-inset-top)+0.5rem)] z-10 flex min-h-touch min-w-touch items-center justify-center rounded-full text-muted hover:bg-surface-alt"
        aria-label="Close"
      >
        <X className="h-5 w-5" />
      </button>

      <div
        className={`mx-auto flex h-full w-full max-w-app flex-col overflow-hidden px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-[calc(env(safe-area-inset-top)+2.5rem)] transition-opacity duration-500 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center text-center">
          <CheckCircle2 className="h-12 w-12 text-primary" strokeWidth={1.75} aria-hidden />
          <h1 id="ride-complete-title" className="mt-2 text-xl font-bold tracking-tight text-content">
            Ride Complete!
          </h1>
          <p className="mt-1 text-sm text-content">
            Well done, {firstName}! <span aria-hidden>🎉</span>
          </p>
          <p className="mt-0.5 text-xs text-muted">
            {riderPhrase}
            <br />
            RideMaadi adventure.
          </p>

          <div className="mt-3 w-full rounded-xl bg-surface px-3 py-2 shadow-sm">
            <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">Route</p>
            <p className="mt-0.5 flex items-start justify-center gap-1 text-sm font-medium leading-snug text-content">
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
              <span className="line-clamp-2">{route}</span>
            </p>
          </div>

          <dl className="mt-3 grid w-full grid-cols-2 gap-2 text-center">
            <div className="rounded-xl bg-surface px-2 py-2 shadow-sm">
              <dt className="sr-only">Distance</dt>
              <dd className="text-base font-bold tabular-nums text-content">{distance}</dd>
            </div>
            <div className="rounded-xl bg-surface px-2 py-2 shadow-sm">
              <dt className="sr-only">Duration</dt>
              <dd className="text-base font-bold tabular-nums text-content">{duration}</dd>
            </div>
            <div className="rounded-xl bg-surface px-2 py-2 shadow-sm">
              <dt className="sr-only">Riders</dt>
              <dd className="text-base font-bold tabular-nums text-content">{riders}</dd>
            </div>
            <div className="rounded-xl bg-surface px-2 py-2 shadow-sm">
              <dt className="sr-only">Stops</dt>
              <dd className="text-base font-bold tabular-nums text-content">{stops}</dd>
            </div>
          </dl>

          <div className="mt-3 w-full rounded-full bg-primary/10 px-3 py-1.5 text-left">
            <p className="text-xs font-semibold text-content">
              <span aria-hidden>{achievement.emoji}</span> {achievement.title}
            </p>
            <p className="text-[0.7rem] text-muted">{achievement.subtitle}</p>
          </div>

          <p className="mt-2 line-clamp-2 px-1 text-xs italic leading-snug text-muted">&ldquo;{memory}&rdquo;</p>
        </div>

        <div className="shrink-0 space-y-2 pt-2">
          <Button fullWidth size="lg" className="rounded-2xl shadow-md shadow-primary/20" onClick={viewSummary}>
            View Ride Summary
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="secondary" fullWidth className="rounded-2xl" onClick={() => void shareRide()}>
              <Share2 className="h-4 w-4" aria-hidden />
              Share Ride
            </Button>
            <Button variant="secondary" fullWidth className="rounded-2xl" onClick={planNext}>
              Plan Next Ride
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
